import { useCallback, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import {
  NEW_POSITION_ID,
  Position as PositionType,
  OC_SAVED_POSITIONS_CACHE_KEY,
  StrikeDeltaObject,
} from 'types/optionsCalculator';
import { GreeksStatsLookback, RawGreeksDataMap, RawStatsDataMap } from 'types';
import TabbedContent from 'components/shared/TabbedContent';
import { TabItem } from 'types/shared';
import { useOptionsCalculatorData } from 'hooks/optionsCalculator/useOptionsCalculatorData';
import { useOptionsCalculatorActions } from 'hooks/optionsCalculator/useOptionsCalculatorActions';
import SavedPositions from './SavedPositions';
import Position from './Position';
import BaseDateSettings from './BaseDateSettings';
import { useQueryClient } from '@tanstack/react-query';
import { getVolFromTableData } from 'util/riskEngine';
import { getAdjustedVolData } from 'util/optionsCalculator';
import { userIsLoggedInState } from 'states/auth';
import { useRecoilValue } from 'recoil';
import { UpsellModal } from 'components/shared';

interface NewPositionContentProps {
  activePositionId: number;
  onSetActivePositionId: (positionId: number) => void;
  newPosition: PositionType;
  onUpdateNewPosition: (updates: Partial<PositionType>) => void;
  greeksLoading?: boolean;
  legIVs: Map<number, number>;
  adjustedLegIVs: Map<number, number>;
  greeksDataMap?: Map<string, RawGreeksDataMap>;
  statsDataMap?: Map<string, RawStatsDataMap>;
  strikeDataPerExpiration: Map<number, StrikeDeltaObject[]>;
  pricesMap?: Map<string, number>;
  onSavePosition: (
    position: PositionType,
    sym: string,
    name: string,
  ) => Promise<PositionType>;
  onMarkAsSaved: () => void;
  setForceDefaultZoom: (forceDefaultZoom: boolean) => void;
}

const NewPositionContent = ({
  greeksLoading = false,
  activePositionId,
  onSetActivePositionId,
  newPosition,
  onUpdateNewPosition,
  legIVs,
  adjustedLegIVs,
  greeksDataMap,
  statsDataMap,
  strikeDataPerExpiration,
  pricesMap,
  onSavePosition,
  onMarkAsSaved,
  setForceDefaultZoom,
}: NewPositionContentProps) => {
  const sym = newPosition.sym;
  const handleSavePosition = useCallback(
    async (position: PositionType, sym: string, name: string) => {
      const result = await onSavePosition(position, sym, name);
      onMarkAsSaved();
      return result;
    },
    [onSavePosition, onMarkAsSaved],
  );

  const { getRiskFreeRate } = useOptionsCalculatorActions();

  return (
    <Stack spacing={3} sx={{ py: 2, height: '100%' }}>
      <Position
        sym={sym}
        position={newPosition}
        getRiskFreeRate={getRiskFreeRate}
        onSetPosition={onUpdateNewPosition}
        isActive={activePositionId === NEW_POSITION_ID}
        greeksLoading={greeksLoading}
        legIVs={legIVs}
        adjustedLegIVs={adjustedLegIVs}
        greeks={greeksDataMap?.get(sym)}
        greekStats={statsDataMap?.get(sym)?.[GreeksStatsLookback.NINETY]}
        currentPrice={pricesMap?.get(sym)}
        onSetActivePositionId={onSetActivePositionId}
        savePosition={handleSavePosition}
        setForceDefaultZoom={setForceDefaultZoom}
        strikeDataPerExpiration={strikeDataPerExpiration}
      />
    </Stack>
  );
};

interface PositionContainerProps {
  tab: string;
  setTab: (tab: string) => void;
  greeksLoading?: boolean;
  legIVs: Map<number, number>;
  // Active position management
  activePositionId: number;
  onSetActivePositionId: (positionId: number) => void;

  // New position draft state
  newPosition: PositionType;
  onUpdateNewPosition: (updates: Partial<PositionType>) => void;

  // Saved positions draft state
  savedPositionDrafts: Map<number, PositionType>;
  onUpdateSavedPositionDraft: (
    positionId: number,
    updates: Partial<PositionType>,
  ) => void;
  onRemoveSavedPositionDraft: (positionId: number) => void;
  onAddSavedPositionDraft: (position: PositionType) => void;

  // Change tracking
  hasUnsavedChanges: Map<number, boolean>;
  onMarkPositionAsSaved: (positionId: number) => void;

  // Saved positions data
  savedPositions: PositionType[];
  isFetchingSavedPositions: boolean;
  setForceDefaultZoom: (forceDefaultZoom: boolean) => void;
  strikeDataPerExpiration: Map<number, StrikeDeltaObject[]>;
}

const PositionContainer = ({
  tab,
  setTab,
  greeksLoading = false,
  activePositionId,
  onSetActivePositionId,
  newPosition,
  onUpdateNewPosition,
  savedPositionDrafts,
  onUpdateSavedPositionDraft,
  onRemoveSavedPositionDraft,
  onAddSavedPositionDraft,
  hasUnsavedChanges,
  onMarkPositionAsSaved,
  savedPositions,
  isFetchingSavedPositions,
  setForceDefaultZoom,
  strikeDataPerExpiration,
  legIVs,
}: PositionContainerProps) => {
  const queryClient = useQueryClient();
  const userIsLoggedIn = useRecoilValue(userIsLoggedInState);
  const [openUpsellModal, setOpenUpsellModal] = useState<boolean>(false);
  const {
    sym,
    legs,
    allGreeksData,
    allStatsData,
    allPrices,
    baseDate,
    allVolDataCall,
    allVolDataPut,
    ivSettings,
  } = useOptionsCalculatorData();
  const {
    savePosition,
    editSavedPosition,
    deleteSavedPosition,
    updateBaseDate,
  } = useOptionsCalculatorActions();

  const adjustedVolDataCall = getAdjustedVolData(
    allVolDataCall.get(sym) ?? [],
    ivSettings,
  );
  const adjustedVolDataPut = getAdjustedVolData(
    allVolDataPut.get(sym) ?? [],
    ivSettings,
  );

  const handleChangeTab = useCallback(
    (tab: string) => {
      if (tab === 'saved' && !userIsLoggedIn) {
        setOpenUpsellModal(true);
      } else {
        setTab(tab);
      }
    },
    [userIsLoggedIn, setTab, setOpenUpsellModal],
  );

  const handleSaveNewPosition = useCallback(
    async (position: PositionType, sym: string, name: string) => {
      const result = await savePosition(position, sym, name);
      await queryClient.invalidateQueries({
        queryKey: [OC_SAVED_POSITIONS_CACHE_KEY],
      });
      onAddSavedPositionDraft(result); // Add to draft state
      setTab('saved');
      return result;
    },
    [savePosition, queryClient, onAddSavedPositionDraft, setTab],
  );

  const handleEditSavedPosition = useCallback(
    async (
      positionId: number,
      position: Partial<PositionType>,
      sym: string,
    ): Promise<PositionType> => {
      const result = await editSavedPosition(position, sym);
      await queryClient.invalidateQueries({
        queryKey: [OC_SAVED_POSITIONS_CACHE_KEY],
      });
      onMarkPositionAsSaved(positionId);
      return result;
    },
    [editSavedPosition, queryClient, onMarkPositionAsSaved],
  );

  const handleDeleteSavedPosition = useCallback(
    async (positionId: number) => {
      await deleteSavedPosition(positionId);
      await queryClient.invalidateQueries({
        queryKey: [OC_SAVED_POSITIONS_CACHE_KEY],
      });
      onRemoveSavedPositionDraft(positionId);
    },
    [deleteSavedPosition, queryClient, onRemoveSavedPositionDraft],
  );

  const adjustedLegIVs = new Map(
    greeksLoading
      ? []
      : (newPosition.option_legs
          .map((leg) => {
            const iv = getVolFromTableData(
              leg.option_type === 'C'
                ? adjustedVolDataCall
                : adjustedVolDataPut,
              leg.strike,
              leg.expiry,
            );
            return [leg.leg_id, iv] as const;
          })
          .filter(([_, iv]) => iv != null) as [number, number][]),
  );

  const mainTabs: TabItem[] = [
    {
      label: 'New Position',
      value: 'new',
      content: (
        <NewPositionContent
          greeksLoading={greeksLoading}
          activePositionId={activePositionId}
          onSetActivePositionId={onSetActivePositionId}
          newPosition={newPosition}
          onUpdateNewPosition={onUpdateNewPosition}
          legIVs={legIVs}
          adjustedLegIVs={adjustedLegIVs}
          greeksDataMap={allGreeksData}
          strikeDataPerExpiration={strikeDataPerExpiration}
          statsDataMap={allStatsData}
          pricesMap={allPrices}
          onSavePosition={handleSaveNewPosition}
          onMarkAsSaved={() => onMarkPositionAsSaved(NEW_POSITION_ID)}
          setForceDefaultZoom={setForceDefaultZoom}
        />
      ),
      actions: [
        <BaseDateSettings
          legs={legs}
          baseDate={baseDate}
          updateBaseDate={updateBaseDate}
        />,
      ],
    },
    {
      label: 'Saved Positions',
      value: 'saved',
      content: userIsLoggedIn ? (
        <SavedPositions
          greeksLoading={greeksLoading}
          greeksForSyms={allGreeksData}
          greekStatsForSyms={allStatsData}
          pricesMap={allPrices}
          activePositionId={activePositionId}
          onSetActivePositionId={onSetActivePositionId}
          savedPositions={savedPositions}
          savedPositionDrafts={savedPositionDrafts}
          onUpdateSavedPositionDraft={onUpdateSavedPositionDraft}
          hasUnsavedChanges={hasUnsavedChanges}
          onEditSavedPosition={handleEditSavedPosition}
          onDeleteSavedPosition={handleDeleteSavedPosition}
          isFetchingSavedPositions={isFetchingSavedPositions}
          setForceDefaultZoom={setForceDefaultZoom}
          strikeDataPerExpiration={strikeDataPerExpiration}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            p: 4,
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" gutterBottom>
            Sign In Required
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Please sign in and subscribe to save and manage your option
            positions.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            With an account, you can save multiple positions, track their
            performance, and access them across devices.
          </Typography>
        </Box>
      ),
      actions: [
        <BaseDateSettings
          legs={legs}
          baseDate={baseDate}
          updateBaseDate={updateBaseDate}
        />,
      ],
    },
  ];

  return (
    <>
      <TabbedContent
        tabs={mainTabs}
        givenTab={tab}
        onChange={handleChangeTab}
        sx={{ px: 0, py: 0, height: '100%' }}
      />
      <UpsellModal
        open={openUpsellModal}
        setOpen={setOpenUpsellModal}
        title="Options Calculator"
        subtitle="Sign in to unlock advanced position management features"
        items={[
          'Save and manage multiple option positions',
          'Access your positions across all devices',
          'Advanced Greeks analysis and visualization',
          'Custom volatility adjustments and scenarios',
        ]}
      />
    </>
  );
};

export default PositionContainer;
